(() => {
  if (customElements.get('nfd-nyc-framing')) return;
  const version = 'nfd-nyc-exact-native-corners-v1';
  const pending = new Map(), cache = new Map(), queue = [];
  let running = 0;
  const classify = pixels => {
    if (!pixels || pixels.length !== 16) return null;
    return [...pixels].every(value => value === 255) ? 'contain' : 'cover';
  };
  const original = (source, base) => {
    try {
      if (typeof source !== 'string' || !/^(https:\/\/|\/\/|\/cdn\/)/.test(source)) return null;
      const url = new URL(source, base), owner = new URL(base);
      if (url.protocol !== 'https:' || url.username || url.password || url.port) return null;
      if (url.hostname === 'cdn.shopify.com') { if (!url.pathname.startsWith('/s/files/')) return null; }
      else if (url.hostname !== owner.hostname || !url.pathname.startsWith('/cdn/shop/')) return null;
      url.searchParams.delete('width'); url.hash = '';
      if ([...url.searchParams.keys()].some(key => key !== 'v') || url.searchParams.getAll('v').length !== 1 || !/^\d+$/.test(url.searchParams.get('v'))) return null;
      return url.href;
    } catch { return null; }
  };
  const pump = () => {
    while (running < 2 && queue.length) {
      const work = queue.shift(); running++;
      Promise.resolve().then(work).finally(() => { running--; pump(); });
    }
  };
  const alive = job => [...job.consumers].some(consumer => consumer());
  const cancelUnused = () => pending.forEach((job, source) => { if (!alive(job)) { pending.delete(source); job.cancel?.(); } });
  const sample = (source, job) => new Promise(resolve => {
    if (!alive(job)) { resolve(null); return; }
    const image = new Image();
    let finished = false;
    const timeout = setTimeout(() => finish(null), 12000);
    const finish = fit => {
      if (finished) return;
      finished = true; clearTimeout(timeout); image.onload = image.onerror = null; image.src = ''; resolve(fit);
    };
    job.cancel = () => finish(null);
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      try {
        const width = image.naturalWidth, height = image.naturalHeight;
        if (!width || !height) { finish(null); return; }
        const canvas = document.createElement('canvas'); canvas.width = 4; canvas.height = 1;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) { finish(null); return; }
        context.imageSmoothingEnabled = false;
        [[0, 0], [width - 1, 0], [0, height - 1], [width - 1, height - 1]].forEach(([x, y], index) => context.drawImage(image, x, y, 1, 1, index, 0, 1, 1));
        finish(classify(context.getImageData(0, 0, 4, 1).data));
      } catch { finish(null); }
    };
    image.onerror = () => finish(null);
    image.src = source;
  });
  const readCache = source => {
    if (cache.has(source)) return cache.get(source);
    try {
      const saved = JSON.parse(localStorage.getItem(version + ':' + source));
      if (saved?.version === version && saved.source === source && ['contain', 'cover'].includes(saved.fit)) return saved.fit;
    } catch {}
    return null;
  };
  const getFit = (source, consumer) => {
    const saved = readCache(source);
    if (saved) return Promise.resolve(saved);
    if (pending.has(source)) { const job = pending.get(source); job.consumers.add(consumer); return job.promise; }
    const job = { consumers: new Set([consumer]) };
    job.promise = new Promise(resolve => queue.push(async () => {
      const fit = await sample(source, job);
      if (fit) {
        cache.set(source, fit);
        try { localStorage.setItem(version + ':' + source, JSON.stringify({ version, source, fit })); } catch {}
      }
      if (pending.get(source) === job) pending.delete(source);
      resolve(fit);
    }));
    pending.set(source, job); pump(); return job.promise;
  };
  customElements.define('nfd-nyc-framing', class extends HTMLElement {
    static get observedAttributes() { return ['data-framing-mode', 'data-product-id']; }
    attributeChangedCallback(name, before, after) {
      if (before === null || before === after || !this.isConnected) return;
      this.disconnectedCallback(); this.connectedCallback();
    }
    connectedCallback() {
      if (this.token || this.dataset.framingMode !== 'browser' || !('IntersectionObserver' in window)) return;
      const token = { active: true }; this.token = token; this.sources = new Map();
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) if (entry.isIntersecting) {
          this.observer?.unobserve(entry.target); this.process(entry.target, token);
        }
      }, { rootMargin: '150px', threshold: 0 });
      this.mutations = new MutationObserver(() => this.scan());
      this.mutations.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-nfd-nyc-source'] });
      this.scan();
    }
    disconnectedCallback() {
      if (this.token) this.token.active = false;
      this.token = null; this.observer?.disconnect(); this.mutations?.disconnect(); this.sources?.clear(); cancelUnused();
    }
    scan() {
      if (!this.token?.active) return;
      for (const slide of this.sources.keys()) if (!this.contains(slide)) { this.observer.unobserve(slide); this.sources.delete(slide); }
      this.querySelectorAll('[data-nfd-nyc-source]').forEach(slide => {
        const source = original(slide.dataset.nfdNycSource, this.ownerDocument.baseURI);
        if (!source || this.sources.get(slide) === source) return;
        this.sources.set(slide, source); delete slide.dataset.nfdNycFit; this.observer.observe(slide);
      });
      cancelUnused();
    }
    process(slide, token) {
      const source = this.sources.get(slide);
      const isCurrent = () => token.active && this.isConnected && this.contains(slide) && this.dataset.framingMode === 'browser' && original(slide.dataset.nfdNycSource, this.ownerDocument.baseURI) === source;
      if (!source || !isCurrent()) return;
      getFit(source, isCurrent).then(fit => { if (fit && isCurrent()) slide.dataset.nfdNycFit = fit; });
    }
  });
})();
